import React, { useState, useEffect } from 'react';
import { Settings, Copy, Check, AlertCircle, Package } from 'lucide-react';
import { AVAILABLE_BIDDERS } from '../config/prebidOptions';

const BidderManager = ({ sites, onApplyToSites, onClose }) => {
  const [selectedBidders, setSelectedBidders] = useState({});
  const [selectedSites, setSelectedSites] = useState([]);
  const [applyMode, setApplyMode] = useState('enable'); // 'enable' or 'disable'
  const [showSuccess, setShowSuccess] = useState(false);

  // Initialize with current bidder status across sites
  useEffect(() => {
    const bidderStatus = {};
    Object.keys(AVAILABLE_BIDDERS).forEach(bidderName => {
      bidderStatus[bidderName] = {
        enabledSites: [],
        totalSites: sites.length
      };
      
      sites.forEach(site => {
        if (site.config?.bidders?.[bidderName]?.enabled) {
          bidderStatus[bidderName].enabledSites.push(site.siteId);
        }
      });
    });
    setSelectedBidders(bidderStatus);
  }, [sites]);

  const handleBidderToggle = (bidderName) => {
    setSelectedBidders(prev => ({
      ...prev,
      [bidderName]: {
        ...prev[bidderName],
        selected: !prev[bidderName]?.selected
      }
    }));
  };

  const handleSiteToggle = (siteId) => {
    setSelectedSites(prev => 
      prev.includes(siteId) 
        ? prev.filter(id => id !== siteId)
        : [...prev, siteId]
    );
  };

  const handleSelectAllSites = () => {
    setSelectedSites(selectedSites.length === sites.length ? [] : sites.map(s => s.siteId));
  };

  const handleSelectAllBidders = () => {
    const allSelected = Object.values(selectedBidders).every(b => b.selected);
    const updated = {};
    Object.keys(selectedBidders).forEach(bidderName => {
      updated[bidderName] = {
        ...selectedBidders[bidderName],
        selected: !allSelected
      };
    });
    setSelectedBidders(updated);
  };

  const handleApply = async () => {
    const biddersToUpdate = Object.keys(selectedBidders).filter(b => selectedBidders[b].selected);
    
    if (biddersToUpdate.length === 0 || selectedSites.length === 0) {
      alert('Please select at least one bidder and one site');
      return;
    }

    const updates = selectedSites.map(siteId => ({
      siteId,
      bidders: biddersToUpdate,
      action: applyMode
    }));

    try {
      await onApplyToSites(updates);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to apply bidder changes:', error);
      alert('Failed to apply bidder changes: ' + error.message);
    }
  };

  const getBidderStatusBadge = (bidderName) => {
    const status = selectedBidders[bidderName];
    if (!status) return null;
    
    const enabledCount = status.enabledSites.length;
    const totalCount = status.totalSites;
    
    if (enabledCount === 0) {
      return <span className="text-xs text-gray-500">Disabled on all sites</span>;
    } else if (enabledCount === totalCount) {
      return <span className="text-xs text-green-600">Enabled on all sites</span>;
    } else {
      return <span className="text-xs text-blue-600">Enabled on {enabledCount}/{totalCount} sites</span>;
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[900px] shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Package size={20} />
            Global Bidder Management
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {showSuccess && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2">
            <Check size={16} className="text-green-600" />
            <span className="text-green-800">Bidder settings applied successfully!</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-6">
          {/* Bidders Selection */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium text-gray-900">Select Bidders</h4>
              <button
                onClick={handleSelectAllBidders}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                {Object.values(selectedBidders).every(b => b.selected) ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="border border-gray-200 rounded-lg max-h-[400px] overflow-y-auto">
              {Object.entries(AVAILABLE_BIDDERS).map(([bidderName, bidderDef]) => (
                <div
                  key={bidderName}
                  className={`p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${
                    selectedBidders[bidderName]?.selected ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => handleBidderToggle(bidderName)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedBidders[bidderName]?.selected || false}
                        onChange={() => {}}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <div>
                        <div className="font-medium text-gray-900">{bidderDef.label}</div>
                        <div className="text-xs text-gray-500">{bidderDef.description}</div>
                      </div>
                    </div>
                    {getBidderStatusBadge(bidderName)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sites Selection */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium text-gray-900">Apply to Sites</h4>
              <button
                onClick={handleSelectAllSites}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                {selectedSites.length === sites.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="border border-gray-200 rounded-lg max-h-[400px] overflow-y-auto">
              {sites.map(site => (
                <div
                  key={site.siteId}
                  className={`p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${
                    selectedSites.includes(site.siteId) ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => handleSiteToggle(site.siteId)}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedSites.includes(site.siteId)}
                      onChange={() => {}}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <div>
                      <div className="font-medium text-gray-900">{site.siteName}</div>
                      <div className="text-xs text-gray-500">{site.domain}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action Selection */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">Action</h4>
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="enable"
                checked={applyMode === 'enable'}
                onChange={(e) => setApplyMode(e.target.value)}
                className="h-4 w-4 text-blue-600"
              />
              <span className="text-gray-700">Enable selected bidders</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="disable"
                checked={applyMode === 'disable'}
                onChange={(e) => setApplyMode(e.target.value)}
                className="h-4 w-4 text-blue-600"
              />
              <span className="text-gray-700">Disable selected bidders</span>
            </label>
          </div>
        </div>

        {/* Summary */}
        {selectedSites.length > 0 && Object.values(selectedBidders).some(b => b.selected) && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-start gap-2">
              <AlertCircle size={16} className="text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <strong>Summary:</strong> {applyMode === 'enable' ? 'Enable' : 'Disable'} {Object.values(selectedBidders).filter(b => b.selected).length} bidder(s) on {selectedSites.length} site(s)
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={selectedSites.length === 0 || !Object.values(selectedBidders).some(b => b.selected)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Copy size={16} />
            Apply Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default BidderManager;